import type { Page } from '@connaro/shared';
import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { Banner, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface Device {
  id: string;
  name: string;
}

interface Diagnosis {
  from: string;
  to: string;
  canCommunicate: boolean;
  findings: Array<{ code: string; status: string; detail: string }>;
}

const STATUS_CHIP: Record<string, string> = {
  ok: 'chip--success',
  warning: 'chip--warning',
  blocker: 'chip--critical',
};

/**
 * "Why can't these two devices communicate?" — pick a source and destination and get an
 * evidence-based diagnosis over the stored configuration. It probes nothing live; every
 * finding is a reason derived from fabric membership, tunnels, overlaps, reachability, and
 * firewall posture.
 */
export function DiagnosticsPage(): JSX.Element {
  const t = useT();
  const devices = useAsync(() => api.get<Page<Device>>('/edge-devices?limit=200'), []);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [result, setResult] = useState<Diagnosis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      setResult(await api.get<Diagnosis>(`/sdwan/connectivity/${from}/${to}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    } finally {
      setBusy(false);
    }
  };

  const items = devices.data?.items ?? [];

  return (
    <section>
      <div className="page-head">
        <h1>{t('diagnostics.title')}</h1>
      </div>

      <div className="card">
        <p>{t('diagnostics.intro')}</p>
        {devices.loading ? (
          <Loading />
        ) : (
          <form className="form form--inline" onSubmit={(e) => void run(e)}>
            <label className="field">
              <span>{t('diagnostics.from')}</span>
              <select value={from} onChange={(e) => setFrom(e.target.value)} required>
                <option value="">—</option>
                {items.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>{t('diagnostics.to')}</span>
              <select value={to} onChange={(e) => setTo(e.target.value)} required>
                <option value="">—</option>
                {items.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit" className="btn btn--primary" disabled={busy || !from || !to}>
              {busy ? t('diagnostics.running') : t('diagnostics.run')}
            </button>
          </form>
        )}
        {error ? <Banner variant="error">{error}</Banner> : null}
      </div>

      {result ? (
        <div className="card">
          <h2>
            {result.canCommunicate ? (
              <span className="chip chip--success">{t('diagnostics.canCommunicate')}</span>
            ) : (
              <span className="chip chip--critical">{t('diagnostics.blocked')}</span>
            )}
          </h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('diagnostics.status')}</th>
                  <th>{t('diagnostics.finding')}</th>
                </tr>
              </thead>
              <tbody>
                {result.findings.map((f, i) => (
                  <tr key={`${f.code}-${i}`}>
                    <td>
                      <span className={`chip ${STATUS_CHIP[f.status] ?? ''}`}>{f.status}</span>
                    </td>
                    <td>{f.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </section>
  );
}
